import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AdminLayout } from '../../layouts/AdminLayout';
import { useClient, useUpdateClient } from '../../hooks';
import { ClientForm } from '../../components/organisms/ClientForm';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { toast } from '../../utils/toast';
import type { UpdateClientDto } from '@lnr-v2/types';

export const EditClientPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { client, loading: loadingClient, error: loadError } = useClient(id);
  const {
    updateClient,
    loading: updating,
    error: updateError,
  } = useUpdateClient();
  const [showError, setShowError] = useState(true);

  const handleSubmit = async (data: UpdateClientDto) => {
    if (!id) return;

    try {
      setShowError(false);
      await updateClient(id, data);
      toast.success('Client updated successfully');
      navigate(`/admin/clients/${id}`);
    } catch (err) {
      setShowError(true);
      // Error is already set in the hook
    }
  };

  const handleCancel = () => {
    navigate(`/admin/clients/${id}`);
  };

  if (loadingClient) {
    return (
      <AdminLayout>
        <LoadingOverlay isLoading={true} />
      </AdminLayout>
    );
  }

  if (loadError || !client) {
    return (
      <AdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <h3 className="text-sm font-medium text-red-800">
              {loadError ? 'Error loading client' : 'Client not found'}
            </h3>
            <p className="mt-1 text-sm text-red-700">
              {loadError?.message || 'The requested client could not be found.'}
            </p>
            <Link
              to="/admin/clients"
              className="mt-2 inline-block text-sm font-medium text-red-800 hover:text-red-700"
            >
              ← Back to clients
            </Link>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link
                to="/admin/clients"
                className="text-gray-500 hover:text-gray-700"
              >
                Clients
              </Link>
            </li>
            <li>
              <svg
                className="w-5 h-5 text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </li>
            <li>
              <Link
                to={`/admin/clients/${id}`}
                className="text-gray-500 hover:text-gray-700"
              >
                {client.name}
              </Link>
            </li>
            <li>
              <svg
                className="w-5 h-5 text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </li>
            <li>
              <span className="text-gray-900 font-medium">Edit</span>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Edit Client</h1>
          <p className="mt-1 text-sm text-gray-600">
            Update the client information below.
          </p>
        </div>

        {/* Error Message */}
        {updateError && showError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error updating client
                </h3>
                <p className="mt-1 text-sm text-red-700">
                  {updateError.message}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <ClientForm
          initialData={client}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={updating}
        />
      </div>
    </AdminLayout>
  );
};
